import { withSearchParams } from "@/lib/utils";
import {
  LIBRARY_PARAM,
  MODULE_PARAM,
  MODULE_TAB_PARAM,
  OVERVIEW,
} from "@/modules/assistants/module-library/use-module-library";
import { lazy, type ReactNode } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";

const GeneralDetails = lazy(() =>
  import("./tabs/general-tab").then((m) => ({
    default: m.GeneralDetails,
  })),
);
const GeneralModel = lazy(() =>
  import("./tabs/general-tab").then((m) => ({ default: m.GeneralModel })),
);
const VoiceTab = lazy(() =>
  import("./tabs/voice-tab").then((m) => ({ default: m.VoiceTab })),
);
const VoiceSettings = lazy(() =>
  import("./tabs/voice-tab").then((m) => ({ default: m.VoiceSettings })),
);
const VoiceModels = lazy(() =>
  import("./tabs/voice-tab").then((m) => ({ default: m.VoiceModels })),
);
const PhoneTab = lazy(() =>
  import("./tabs/phone-tab").then((m) => ({ default: m.PhoneTab })),
);
const WebsiteTab = lazy(() =>
  import("./tabs/website-tab").then((m) => ({ default: m.WebsiteTab })),
);
const WebsiteLook = lazy(() =>
  import("./tabs/website-tab").then((m) => ({ default: m.WebsiteLook })),
);
const WebsiteLimits = lazy(() =>
  import("./tabs/website-tab").then((m) => ({
    default: m.WebsiteLimits,
  })),
);
const PhoneTransfers = lazy(() =>
  import("./tabs/phone-tab").then((m) => ({
    default: m.PhoneTransfers,
  })),
);
const PhoneExceptions = lazy(() =>
  import("./tabs/phone-tab").then((m) => ({
    default: m.PhoneExceptions,
  })),
);
// exported because the overview opens these two in a dialog as well
export const GreetingsTab = lazy(() =>
  import("./tabs/greetings-tab").then((m) => ({
    default: m.GreetingsTab,
  })),
);
export const PromptTab = lazy(() =>
  import("./tabs/prompt-tab").then((m) => ({ default: m.PromptTab })),
);
const SkillsTab = lazy(() =>
  import("./tabs/skills-tab").then((m) => ({ default: m.SkillsTab })),
);
const KnowledgeTab = lazy(() =>
  import("./tabs/knowledge-tab").then((m) => ({
    default: m.KnowledgeTab,
  })),
);
const IntakeTab = lazy(() =>
  import("./tabs/intake-tab").then((m) => ({ default: m.IntakeTab })),
);
const GlossaryTab = lazy(() =>
  import("./tabs/glossary-tab").then((m) => ({ default: m.GlossaryTab })),
);
const ConnectorsTab = lazy(() =>
  import("./tabs/connectors-tab").then((m) => ({
    default: m.ConnectorsTab,
  })),
);
const VoiceTuningTab = lazy(() =>
  import("./tabs/voice-tuning-tab").then((m) => ({
    default: m.VoiceTuningTab,
  })),
);
const VoiceExperimentsTab = lazy(() =>
  import("./tabs/voice-experiments-tab").then((m) => ({
    default: m.VoiceExperimentsTab,
  })),
);
const PermissionsTab = lazy(() =>
  import("./tabs/permissions-tab").then((m) => ({
    default: m.PermissionsTab,
  })),
);

// the module settings dialog picks a page's content from here by its key
export const assistantSettingsTabs: Record<string, ReactNode> = {
  details: <GeneralDetails />,
  model: <GeneralModel />,
  "voice-models": <VoiceModels />,
  "voice-settings": <VoiceSettings />,
  voice: <VoiceTab />,
  phone: <PhoneTab />,
  "phone-exceptions": <PhoneExceptions />,
  "phone-transfers": <PhoneTransfers />,
  website: <WebsiteTab />,
  "website-look": <WebsiteLook />,
  "website-limits": <WebsiteLimits />,
  permissions: <PermissionsTab />,
  greetings: <GreetingsTab />,
  prompt: <PromptTab />,
  skills: <SkillsTab />,
  knowledge: <KnowledgeTab />,
  intake: <IntakeTab />,
  glossary: <GlossaryTab />,
  connectors: <ConnectorsTab />,
  "voice-tuning": <VoiceTuningTab />,
  "voice-experiments": <VoiceExperimentsTab />,
};

/**
 * LegacyModuleSettingsRedirect maps old /assistants/:id/:tab urls onto the
 * dialog search params, so bookmarks keep working.
 */
export function LegacyModuleSettingsRedirect() {
  const { assistantId, "*": splat } = useParams();
  const search = new URLSearchParams({
    [MODULE_PARAM]: assistantId ?? "",
    [MODULE_TAB_PARAM]: splat?.split("/")[0] || OVERVIEW,
  });
  return (
    <Navigate to={{ pathname: "/chat", search: search.toString() }} replace />
  );
}

/**
 * ModuleLibraryRedirect keeps /assistants working now that the library is a
 * palette rather than a page: it lands on the chat with the library open, and
 * hands old dialog links straight to the module settings instead.
 */
export function ModuleLibraryRedirect() {
  const [searchParams] = useSearchParams();
  const search = searchParams.has(MODULE_PARAM)
    ? searchParams.toString()
    : withSearchParams(searchParams, { [LIBRARY_PARAM]: "1" });

  return <Navigate to={{ pathname: "/chat", search }} replace />;
}
